import React, { useEffect, useRef, useState, useContext } from "react";
import styles from "./ActivationChannels.module.css";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { AuthContext } from "../../contexts/auth-context";
import SearchableDropdown from "../SearchableDropdown";
import DV360Icon from "../../assets/dv360.svg";
import PubMaticIcon from "../../assets/pubMatic.png";
import GoogleAdsIcon from "../../assets/googleads.png";
import AWSIcon from "../../assets/aws.svg";
import TTDIcon from "../../assets/ttd.svg";
import MetaIcon from "../../assets/meta.svg";
import AzureIcon from "../../assets/azure.png";
import MediasmartIcon from "../../assets/mediasmart.png";
import GCPIcon from "../../assets/gcp.png";
import SFTPIcon from "../../assets/sftp.jpg";
import CloudIcon from "../../assets/cloud.png";
import config from "../../config";
import SearchIcon from "../../assets/search.svg";
import CrossIcon from "../../assets/cancelled.svg";
import { useLimitsUsage } from "../../hooks/use-limits-usage";

type channelType = {
  id: string;
  name: string;
  url: string;
  status: string;
  icon: string;
  key?: string;
  channelType?: string;
}[];

const channels = [
  {
    id: "dv360",
    name: "DV360",
    url: "Display & Video 360",
    status: "Ready",
    icon: DV360Icon,
  },
  {
    id: "pubmatic",
    name: "PubMatic",
    url: "PubMatic",
    status: "Ready",
    icon: PubMaticIcon,
  },
  {
    id: "googleads",
    name: "Google Ads",
    url: "Google Ads",
    status: "Ready",
    icon: GoogleAdsIcon,
  },
  {
    id: "ttd",
    name: "The Trade Desk",
    url: "The Trade Desk",
    status: "",
    icon: TTDIcon,
  },
  {
    id: "meta",
    name: "Meta Ad Server",
    url: "Meta",
    status: "Ready",
    icon: MetaIcon,
  },
  {
    id: "aws",
    name: "AWS",
    url: "AWS S3 Bucket",
    status: "Ready",
    icon: AWSIcon,
  },
  {
    id: "azure",
    name: "Azure",
    url: "Azure Blob Container",
    status: "Ready",
    icon: AzureIcon,
  },
  {
    id: "mediasmart",
    name: "Mediasmart",
    url: "Mediasmart",
    status: "Ready",
    icon: MediasmartIcon,
  },
  {
    id: "gcp",
    name: "GCP",
    url: "Google Cloud Storage",
    status: "Ready",
    icon: GCPIcon,
  },
  {
    id: "sftp",
    name: "SFTP",
    url: "SFTP",
    status: "Ready",
    icon: SFTPIcon,
  },
];
const cloudChannels = ["gcp", "aws", "azure", "sftp"];

const ActivationPopup = ({ onClose, queryData, onSuccess, onFailure }) => {
  const [dynamicChannels, setDynamicChannels] = useState<channelType>(channels);
  const [filteredChannels, setFilteredChannels] = useState<channelType>(channels);
  // Channels matched against the org's configured ad servers, before the
  // FR-20 enabled_connectors filter - used only to tell "org has no ad
  // servers configured at all" apart from "ad servers exist but none are
  // enabled for this query objective" (AC4/AC5), since dynamicChannels alone
  // looks empty in both cases.
  const [allMatchedChannels, setAllMatchedChannels] = useState<channelType>([]);
  const [searchChannel, setSearchChannel] = useState("");
  const [channelSearchActive, setChannelSearchActive] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);

  const handleCloseSearch = () => {
    if (!channelSearchActive || isSearchClosing) return;
    setIsSearchClosing(true);
    setTimeout(() => {
      setChannelSearchActive(false);
      setIsSearchClosing(false);
    }, 300);
  };
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [audienceName, setAudienceName] = useState(queryData?.campaign_name);
  const [segId, setSegId] = useState("");
  const [expiryDuration, setExpiryDuration] = useState("");
  const [recordsToActivate, setRecordsToActivate] = useState(
    queryData?.differential_privacy_count_total
  );
  const [fileFormat, setFileFormat] = useState("");
  const totalRecords = queryData?.differential_privacy_count_total;
  const [audienceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [recordsPercentage, setRecordsPercentage] = useState(100);
  const userRole = localStorage.getItem("userRole");
  const isPlatformOrAppAdmin =
    userRole === config.superUserRole ||
    userRole === config.applicationAdminRole;
  var organizationId = localStorage.getItem("organizationkey");
  var federationId = localStorage.getItem("federationkey");
  const {
    getAdserversByOrgId,
    putEngage,
    user,
    getOrganizations,
    getConfig,
    applicationId,
  } = useContext(AuthContext);

  const { given_name, family_name, email } = user || {};
  var user_name = given_name + " " + family_name;

  const [selectedOrg, setSelectedOrg] = useState<string>("");

  // Proactive connector-availability check (FR-20 Task 3): the backend
  // rejects Next/engage with "No activation connectors enabled..." for any
  // connector not in this query objective's enabled_connectors allowlist, but
  // the channel table below still showed every technically-connected
  // channel as selectable. undefined (not yet loaded / fetch failed) means
  // "don't restrict yet" - enforcement itself always happens server-side on
  // submit regardless, this is purely a heads-up. Mirrors the same
  // isPlatformOrAppAdmin -> selectedOrg switch the activationData fetch and
  // handleNextClick's org_id already use, since a platform/app admin is
  // checking connector availability for selectedOrg, not their own org.
  const effectiveOrgId = isPlatformOrAppAdmin ? selectedOrg : organizationId;
  const { fetchUsage: fetchLimitsUsageData } = useLimitsUsage();
  const [enabledConnectors, setEnabledConnectors] = useState<
    string[] | undefined
  >(undefined);

  useEffect(() => {
    setEnabledConnectors(undefined);
    if (!effectiveOrgId || !federationId || !queryData?.objective) {
      return;
    }
    let cancelled = false;
    fetchLimitsUsageData([queryData.objective], {
      organizationId: effectiveOrgId,
      federationId,
    }).then((results) => {
      if (!cancelled) {
        setEnabledConnectors(
          results[0]?.usage?.activation_usage?.enabled_connectors,
        );
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, federationId, queryData?.objective]);

  const getAdserversByOrgIdFn = useAsyncFn({
    deferFn: getAdserversByOrgId,
  });
  const putEngageFn = useAsyncFn({
    deferFn: putEngage,
  });
  const getConfigFn = useAsyncFn({
    deferFn: getConfig,
    organizationId,
  });
  useEffect(() => {
    if (organizationId) {
      getConfigFn.run({ organizationId }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);
  const identityTypeMap = new Map<string, string>(
    (getConfigFn.data?.message?.canonical_identity_types ?? []).map(
      ({ key, value }: { key: string; value: string }) => [key, value],
    ),
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [activationData, setActivationData] = useState([]);

  useEffect(() => {
    if (!isPlatformOrAppAdmin) {
      setLoading(true);
      getAdserversByOrgIdFn
        .run({ organizationId })
        .then((data: any) => setActivationData(data?.ad_server ?? []))
        .catch((err) => console.error("Error fetching data:", err))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPlatformOrAppAdmin && selectedOrg) {
      setActivationData([]);
      setLoading(true);
      getAdserversByOrgIdFn
        .run({ organizationId: selectedOrg })
        .then((data: any) =>
          setActivationData(
            (data?.ad_server ?? []).map((item) => ({ ...item, orgId: selectedOrg }))
          )
        )
        .catch((err) => console.error("Handled gracefully:", err))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    if (!activationData) return;
    const isEnrichment = queryData?.objective === "enrichment";
    const tableData = channels
      .map((channel) => {
        // Enrichment queries may only engage Cloud & Storage endpoints
        if (isEnrichment && !cloudChannels.includes(channel.id)) {
          return null;
        }

        const matchedChannels = activationData?.filter(
          (ready) =>
            ready.cloud === channel.id ||
            (!isEnrichment && ready.ad_server === channel.id)
        );

        if (!matchedChannels?.length) return null;

        // channelType is captured before the ...matched spread since matched
        // (an org's configured ad-server entry) may carry its own `id` that
        // would otherwise silently shadow the static channel's id here.
        return matchedChannels.map((matched, index) => ({
          ...channel,
          ...matched,
          status: "Ready",
          key: `${channel.id}-${matched.name}-${index}`,
          channelType: channel.id,
        }));
      })
      .flat()
      .filter(Boolean);
    setAllMatchedChannels([...tableData]);
    // FR-20 Task 3 AC4: connectors not enabled for this query objective are
    // hidden entirely, not shown grayed out - enabledConnectors===undefined
    // (still loading) permissively shows everything, matching the previous
    // disabled-flag default.
    // const enabledOnly =
    //   enabledConnectors === undefined
    //     ? tableData
    //     : tableData.filter((c) => enabledConnectors.includes(c.channelType));
    // setDynamicChannels([...enabledOnly]);
    // setFilteredChannels([...enabledOnly]);
    setDynamicChannels([...tableData]);
    setFilteredChannels([...tableData]);
  }, [activationData, enabledConnectors, queryData?.objective]);

  const handleCheckboxChange = (channelId) => {
    setSelectedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleNextClick = async () => {
    setIsLoading(true); // Show loader

    const matchedAdServers = (activationData as any[]).filter((server) =>
      selectedChannels.includes(server.name)
    );
    const hasPubmatic = matchedAdServers.some(
      (server) => String(server.ad_server ?? "").toLowerCase() === "pubmatic",
    );
    const hasAdServer = matchedAdServers.some(
      (server) => String(server.ad_server ?? "") !== "",
    );

    const body = {
      query_id: queryData.id,
      org_id: organizationId,
      user_name: user_name,
      audience_name: audienceName,
      days_to_expiry: hasAdServer ? Number(expiryDuration) || 0 : 0,
      result_file_format: fileFormat,
      user_email: email,
      identifier_type: queryData?.canonical_identity_type,
      audience_id: audienceId,
      ad_server_names: selectedChannels,
      max_ids: Number(recordsToActivate),
      max_ids_flag: Number(recordsToActivate) < Number(totalRecords),
      ...(hasPubmatic ? { seg_id: segId.trim() } : {}),
      activation_connectors: [
        {
          org_id: isPlatformOrAppAdmin ? selectedOrg : organizationId,
          ad_servers: matchedAdServers,
          ad_server_names: selectedChannels,
        },
      ],
    };
    try {
const results = await putEngageFn.run({ body, role: userRole, organizationId: organizationId, applicationId });
      const STATUSES = ["No-op", "Success", "Error"];
      onSuccess(
        Object.entries(results)
          .map(([key, value]) => {
            const str = String(value);
            const status = STATUSES.find((s) => str.startsWith(s)) ?? "";
            const afterStatus = str.slice(status.length);
            const colonIdx = afterStatus.indexOf(": ");
            const message = colonIdx >= 0 ? afterStatus.slice(colonIdx + 2) : afterStatus;
            return status ? `${status} · ${key}: ${message}` : `${key}: ${str}`;
          })
          .join("\n")
      );
    } catch (error) {
      console.error("Failed to update audience:", error);
      // putEngage throws a plain Error (fetch-based), not an axios error -
      // there is no error.response; the parsed backend message lives on
      // error.message (e.g. "Activation limit reached (100/100). Contact
      // administrator." or "No activation connectors enabled for this
      // query objective. Contact administrator.").
      const message = error?.message;
      onFailure(message || null);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const matchedSelectedServers = (activationData as any[]).filter((server) =>
    selectedChannels.includes(server.name)
  );
  const showDaysToExpiry = matchedSelectedServers.some((s) => s.ad_server !== "");
  const showFileFormat = matchedSelectedServers.some(
    (s) =>
      s.cloud !== "" && String(s.ad_server ?? "").toLowerCase() !== "pubmatic",
  );
  const showSegId = matchedSelectedServers.some(
    (s) => String(s.ad_server ?? "").toLowerCase() === "pubmatic",
  );
  const pubmaticWasSelected = useRef(false);
  useEffect(() => {
    if (showSegId && !pubmaticWasSelected.current && expiryDuration === "") {
      setExpiryDuration("1");
    }
    pubmaticWasSelected.current = showSegId;
  }, [showSegId, expiryDuration]);

  const isNextDisabled =
    !audienceName ||
    (showDaysToExpiry && !expiryDuration) ||
    (showSegId && !segId.trim()) ||
    !recordsToActivate ||
    selectedChannels.length === 0;

  useEffect(() => {
    if (!totalRecords || recordsToActivate == null) return;
    const ratio = recordsToActivate / totalRecords;
    let pct = (ratio * 100).toFixed(2);

    if (recordsToActivate === 0) pct = "0.00";
    else if (pct === "0.00") pct = "0.01";
    else if (pct === "100.00" && recordsToActivate !== totalRecords)
      pct = "99.99";

    setRecordsPercentage(Number(pct));
  }, [recordsToActivate, totalRecords]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9.]/g, "");
    let num = parseFloat(val);

    if (!isNaN(num)) {
      if (num > 100) num = 100;
      if (num < 0) num = 0;
      setRecordsPercentage(num);
      setRecordsToActivate(Math.round((num / 100) * totalRecords));
    } else {
      setRecordsPercentage(0);
      setRecordsToActivate(0);
    }
  };

  const searchChannels = (input) => {
    setSearchChannel(input);
    const searchedChannels = [...dynamicChannels].filter((channel) =>
      channel.name.match(input)
    );
    setFilteredChannels(searchedChannels);
  };
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popupContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.popupHeader}>
          <h3 className={styles.popupTitleText}>
            {isLoading ? "Activating..." : "Select Activation Channels"}
          </h3>
          {!isLoading && (
            <button className={styles.popupCloseBtn} onClick={onClose} aria-label="Close">✕</button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.popupLoadingOverlay}>
            <div className={styles.popupLoadingSpinner} />
          </div>
        ) : (
        <>
        <div className={styles.popupBody}>

          {/* ── Available Channels ── */}
          <div className={styles.channelSectionHeader}>
            <p className={`${styles.sectionLabel} ${styles.sectionLabelFlush}`}>Available Channels</p>
            {isPlatformOrAppAdmin && (
              <SearchableDropdown<{ id: string; name: string }>
                fetchOptions={(name) =>
                  getOrganizations({ args: { name, page_size: 8 } })
                }
                onSelect={(org) => setSelectedOrg(org.id)}
                onClear={() => setSelectedOrg("")}
                getLabel={(org) => org.name}
                getValue={(org) => org.id}
                selectedValue={selectedOrg}
                placeholder="Search organization…"
                className={styles.orgSelect}
              />
            )}
          </div>

          <div className={`${styles.tableContainer} ${styles.channelTableCard}`}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th className={styles.tableData}>
                    <div className={styles.searchTableHeader} onClick={handleCloseSearch}>
                      {!channelSearchActive && !isSearchClosing && (
                        <img src={SearchIcon} alt="Search" className={styles.searchIcon}
                          onClick={(e) => { e.stopPropagation(); setChannelSearchActive(true); }}
                        />
                      )}
                      {(channelSearchActive || isSearchClosing) && (
                        <div className={styles.searchInputWrapper}>
                          <input
                            placeholder="Search activation channel"
                            value={searchChannel}
                            onChange={(e) => searchChannels(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className={isSearchClosing ? styles.searchInputClosing : styles.searchInput}
                          />
                          <img src={CrossIcon} alt="Clear" className={styles.cross}
                            onClick={(e) => { e.stopPropagation(); searchChannels(""); handleCloseSearch(); }}
                          />
                        </div>
                      )}
                      <span className={styles.destinationHeader}>Destination / Channel Name</span>
                    </div>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {isPlatformOrAppAdmin && !selectedOrg ? (
                  <tr><td colSpan={2} className={styles.popupTableEmpty}><i>Please select an organization to view its connectors.</i></td></tr>
                ) : loading ? (
                  <tr><td colSpan={2} className={styles.loaderRow}><span className={styles.loader}></span></td></tr>
                ) : allMatchedChannels.length === 0 ? (
                  <tr><td colSpan={2} className={styles.popupTableEmpty}><i>No connectors available at the moment.</i></td></tr>
                ) : dynamicChannels.length === 0 ? (
                  // FR-20 Task 3 AC5: ad servers are configured, but none of
                  // them are enabled for this query objective.
                  <tr><td colSpan={2} className={styles.popupTableEmpty}><i>No activation connectors enabled for this query objective. Contact administrator.</i></td></tr>
                ) : (
                  filteredChannels.map((channel) => channel && (
                    <tr
                      key={channel.key}
                      className={channel.status === "Ready" ? styles.readyRow : styles.defaultRow}
                    >
                      <td className={styles.channelCell}>
                        <div className={styles.channelWrapper}>
                          <input type="checkbox" className={styles.checkbox}
                            checked={selectedChannels.includes(channel.name)}
                            onChange={() => handleCheckboxChange(channel.name)}
                          />
                          <img
                            src={channel.icon}
                            alt="logo"
                            className={`${styles.logoPlaceholder} ${
                              channel.channelType === "pubmatic" ||
                              channel.id === "pubmatic"
                                ? styles.logoPlaceholderWide
                                : ""
                            }`}
                          />
                          <div className={styles.channelDetails}>
                            <div className={styles.channelName}>
                              {channel.name}
                              {cloudChannels.includes(channel.id) && (
                                <img src={CloudIcon} className={styles.cloudIcon} alt="cloud" />
                              )}
                            </div>
                            <div className={styles.channelUrl}>{channel.url}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={channel.status === "Ready" ? styles.status : undefined}>
                          {channel.status}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Channel Settings (contextual — appears on channel selection) ── */}
          {(showDaysToExpiry || showFileFormat || showSegId) && (
            <div className={styles.channelSettingsPanel}>
              <p className={styles.channelSettingsPanelLabel}>Channel Settings</p>
              <div className={styles.formGrid}>
                {showSegId && (
                  <>
                    <label className={`${styles.formGridLabel} ${styles.fieldSlideIn}`}>Segment Id*</label>
                    <div className={styles.fieldSlideIn}>
                      <input
                        type="text"
                        className={styles.inputField}
                        value={segId}
                        onChange={(e) => setSegId(e.target.value)}
                        placeholder="Enter Segment Id"
                      />
                      <p className={styles.fieldHint}>Applicable to PubMatic only</p>
                    </div>
                  </>
                )}
                {showDaysToExpiry && (
                  <>
                    <label className={`${styles.formGridLabel} ${styles.fieldSlideIn}`}>Days to Expiry*</label>
                    <div className={styles.fieldSlideIn}>
                      <input
                        type="text"
                        className={styles.inputField}
                        value={expiryDuration}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (val === "" || Number(val) <= 120) setExpiryDuration(val);
                        }}
                        inputMode="numeric"
                        placeholder="Enter number of days"
                      />
                      <p className={styles.fieldHint}>Applicable to activation channels only</p>
                    </div>
                  </>
                )}
                {showFileFormat && (
                  <>
                    <label className={`${styles.formGridLabel} ${styles.fieldSlideIn}`}>File Format</label>
                    <div className={styles.fieldSlideIn}>
                      <div className={styles.filesRadioGroup}>
                        {[{ value: "csv", label: "CSV" }, { value: "json", label: "JSON" }].map(({ value, label }) => (
                          <label key={value} className={styles.radioLabel}>
                            <input
                              type="radio"
                              name="fileFormat"
                              value={value}
                              checked={fileFormat === value}
                              onChange={(e) => setFileFormat(e.target.value)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                      <p className={styles.fieldHint}>Applicable to Cloud & Storage endpoints only. Default is JSON.</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <hr className={styles.sectionDivider} />

          {/* ── Audience Details ── */}
          <p className={styles.sectionLabel}>Audience Details</p>
          <div className={styles.formGrid}>
            <label className={styles.formGridLabel}>Audience Name*</label>
            <input
              type="text"
              className={styles.inputField}
              onChange={(e) => setAudienceName(e.target.value)}
              value={audienceName}
            />

            <label className={styles.formGridLabel}>ID Type</label>
            <p className={styles.readOnlyFieldInline}>
              {identityTypeMap.get(queryData?.canonical_identity_type) ?? queryData?.canonical_identity_type}
            </p>
          </div>

          <hr className={styles.sectionDivider} />

          {/* ── Activation Settings ── */}
          <p className={styles.sectionLabel}>Activation Settings</p>
          <div className={styles.formGrid}>
            <label className={styles.formGridLabel}>Records to Engage*</label>
            <div className={styles.recordsInlineRow}>
              <div className={styles.recordsInputGroup}>
                <input
                  type="text"
                  className={styles.recordsCountInput}
                  value={recordsToActivate != null ? Number(recordsToActivate).toLocaleString() : ""}
                  onChange={(e) => {
                    let val = Number(e.target.value.replace(/,/g, ""));
                    if (isNaN(val) || val < 0) val = 0;
                    if (val > totalRecords) val = totalRecords;
                    setRecordsToActivate(val);
                    const pct = totalRecords ? (val / totalRecords) * 100 : 0;
                    setRecordsPercentage(Number(pct.toFixed(2)));
                  }}
                  inputMode="numeric"
                />
                <span className={styles.recordsSuffix}>of {totalRecords != null ? Number(totalRecords).toLocaleString() : "0"}</span>
              </div>
              <div className={styles.recordsVertDivider} />
              <div className={styles.recordsInputGroup}>
                <input
                  type="text"
                  className={styles.recordsPctInput}
                  value={recordsPercentage}
                  onChange={handleChange}
                  inputMode="numeric"
                />
                <span className={styles.recordsSuffix}>%</span>
              </div>
            </div>
          </div>

        </div>

        {/* ── Sticky Footer ── */}
        <div className={styles.popupFooter}>
          <button className={styles.cancelButton} onClick={() => { setSelectedChannels([]); onClose(); }}>
            Cancel
          </button>
          <button className={styles.nextButton} onClick={handleNextClick} disabled={isNextDisabled || isLoading}>
            Next
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ActivationPopup;
